import React, { useEffect, useRef, useState } from 'react';
import { useQuery } from 'react-query';
import { Navigate, useParams } from 'react-router-dom';

import styled from 'styled-components';

import { COMING_SOON_INTERESTS_QUERY_KEY, getComingSoonFeatureInterests } from 'apis/ComingSoonFeaturesAPI';
import useRecommendationsContext from 'components/AiCompetitiveIntelligence/RecommendationsContext';
import { canAccessComingSoonFeatures, hasAISpeedToLeadFlag } from 'helpers/permissions';
import { useAppSelector } from 'store/hook';

import ComingSoonBenefits from './ComingSoonBenefits';
import ComingSoonCallToAction from './ComingSoonCallToAction';
import ComingSoonInterestModal from './ComingSoonInterestModal';
import ComingSoonStats from './ComingSoonStats';
import ComingSoonSteps from './ComingSoonSteps';
import { SectionTitle } from './ComingSoonStyles';
import { COMING_SOON_FEATURE, COMING_SOON_FEATURES, ComingSoonFeatureKey } from './constants';

const Panel = styled.div`
  border-radius: 10px;
  padding: 50px 60px;
  background-image: linear-gradient(
    73deg,
    var(--market-position-gradient-start),
    var(--market-position-gradient-mid) 57%,
    var(--market-position-gradient-end) 100%
  );
  color: var(--white);
`;

const Eyebrow = styled.div`
  font-size: 14px;
  text-transform: uppercase;
  color: var(--white-60);

  span {
    color: var(--positive-green);
  }
`;

const Title = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 10px 0 25px;
  font-family: var(--font-bold);
  font-size: 40px;

  img {
    width: 40px;
    height: 40px;
  }

  span {
    color: var(--flamingo-pink);
  }
`;

const Description = styled.div`
  max-width: 800px;
  font-size: 16px;
  color: var(--white-60);
`;

const ComingSoonFeaturePage = () => {
  const { feature: featureKey } = useParams();
  const panelRef = useRef<HTMLDivElement>(null);
  const { communityOptions } = useRecommendationsContext();
  const { currentUser } = useAppSelector((state) => state.auth);
  const isComingSoonEnabled = canAccessComingSoonFeatures(currentUser);
  const releasedComingSoonFeatures: ComingSoonFeatureKey[] = hasAISpeedToLeadFlag(currentUser)
    ? [COMING_SOON_FEATURE.SPEED_TO_LEAD]
    : [];
  const features = isComingSoonEnabled
    ? COMING_SOON_FEATURES.filter((item) => !releasedComingSoonFeatures.includes(item.key))
    : [];
  const { data: interests = [] } = useQuery({
    queryKey: [COMING_SOON_INTERESTS_QUERY_KEY],
    queryFn: getComingSoonFeatureInterests,
    enabled: isComingSoonEnabled,
    refetchOnWindowFocus: false,
  });
  const [isModalOpen, setIsModalOpen] = useState(false);
  const feature = features.find((item) => item.key === featureKey);

  useEffect(() => {
    for (let el = panelRef.current?.parentElement; el; el = el.parentElement) {
      el.scrollTop = 0;
    }
  }, [featureKey]);

  if (!feature) {
    return <Navigate to="/ai-competitive-intelligence" replace />;
  }

  const interest = interests.find((item) => item.feature === feature.key);
  const pilotCommunityNames = communityOptions
    .filter((option) => interest?.communities.includes(Number(option.value)))
    .map((option) => option.label)
    .join(' and ');

  return (
    <>
      <Panel ref={panelRef}>
        <Eyebrow>AI Competitive Intelligence · {feature.isPilot ? <span>Ready to pilot</span> : 'Coming soon'}</Eyebrow>
        <Title>
          <img src="/icons/ai-blob.svg" alt="" />
          {feature.titlePrefix} <span>{feature.titleHighlight}</span>
        </Title>
        <Description>{feature.description}</Description>
        <SectionTitle>Why it matters · What the research says</SectionTitle>
        <ComingSoonStats stats={feature.stats} />
        <SectionTitle>How it works</SectionTitle>
        <ComingSoonSteps steps={feature.steps} />
        <SectionTitle>What you get?</SectionTitle>
        <ComingSoonBenefits benefits={feature.benefits} />
        <ComingSoonCallToAction
          feature={feature}
          interest={interest}
          pilotCommunityNames={pilotCommunityNames}
          onApply={() => setIsModalOpen(true)}
        />
      </Panel>
      {isModalOpen && <ComingSoonInterestModal feature={feature} onClose={() => setIsModalOpen(false)} />}
    </>
  );
};

export default ComingSoonFeaturePage;
